
class ProductPersonalization extends HTMLElement {
  connectedCallback() {
    this.uploadState = new WeakMap();

    this.addEventListener('change', this.handleChange);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('click', this.handleClick);

    this.querySelectorAll('[data-multi-upload]').forEach(upload => {
      const state = this.uploadState?.get(upload);

      if (state) {
        state.urls.forEach(url => URL.revokeObjectURL(url));
      }
    });
  }

  handleChange = (event) => {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) return;

    // Keep the original color picker behavior.
    if (input.type === 'color') {
      const output = input
        .closest('[data-personalization-field]')
        ?.querySelector('[data-color-output]');

      if (output) {
        output.textContent = input.value.toUpperCase();
      }

      return;
    }

    if (input.matches('[data-personalization-file-picker]')) {
      this.addPhotos(input);
      return;
    }

    // Fallback for any existing single-file input.
    if (input.type === 'file') {
      this.handleSingleFile(input);
    }
  };

  handleSingleFile(input) {
    const field = input.closest('[data-personalization-field]');
    const fileName = field?.querySelector('[data-file-name]');
    const originalFileName = field?.querySelector('[data-original-file-name]');
    const file = input.files?.[0];

    input.setCustomValidity('');

    if (file) {
      const maxFileMb = Number(input.dataset.maxFileMb || 10);

      if (file.size > maxFileMb * 1024 * 1024) {
        input.setCustomValidity(
          `Please choose a file smaller than ${maxFileMb} MB.`
        );
        input.reportValidity();
      }
    }

    if (fileName) {
      fileName.textContent = file ? file.name : 'No file selected';
    }

    if (originalFileName instanceof HTMLInputElement) {
      originalFileName.value = file ? file.name : '';
    }
  }

  handleClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const button = target.closest('[data-remove-photo]');
    if (!button) return;

    event.preventDefault();

    const upload = button.closest('[data-multi-upload]');
    if (!upload) return;

    const state = this.getState(upload);
    const index = Number(button.dataset.removePhoto);

    if (!Number.isInteger(index)) return;
    if (index < 0 || index >= state.files.length) return;

    state.files.splice(index, 1);

    this.renderPhotos(upload);

    const status = upload.querySelector('[data-file-status]');
    if (status) status.textContent = '';
  };

  getState(upload) {
    let state = this.uploadState.get(upload);

    if (!state) {
      state = {
        files: [],
        urls: []
      };

      this.uploadState.set(upload, state);
    }

    return state;
  }

  addPhotos(picker) {
    const upload = picker.closest('[data-multi-upload]');
    if (!upload) return;

    const state = this.getState(upload);

    // Limit is set separately in personalization-field.liquid:
    // Mascot image: 5
    // Other file fields: 10
    const maxFiles = Number(upload.dataset.maxFiles || 10);
    const maxMb = Number(picker.dataset.maxFileMb || 10);

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/webp'
    ];

    const selected = Array.from(picker.files || []);
    const errors = [];

    for (const file of selected) {
      if (state.files.length >= maxFiles) {
        errors.push(`Maximum ${maxFiles} photos allowed.`);
        break;
      }

      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Unsupported file type.`);
        continue;
      }

      if (file.size > maxMb * 1024 * 1024) {
        errors.push(`${file.name}: Maximum ${maxMb} MB per photo.`);
        continue;
      }

      if (file.size === 0) {
        errors.push(`${file.name}: Empty file is not allowed.`);
        continue;
      }

      const duplicate = state.files.some(existing =>
        existing.name === file.name &&
        existing.size === file.size &&
        existing.lastModified === file.lastModified
      );

      if (duplicate) continue;

      state.files.push(file);
    }

    // Allow selecting more photos in subsequent selections.
    picker.value = '';

    this.renderPhotos(upload);

    const status = upload.querySelector('[data-file-status]');
    if (status) status.textContent = errors.join(' ');
  }

  renderPhotos(upload) {
    const state = this.getState(upload);

    const preview = upload.querySelector('[data-file-preview]');
    const count = upload.querySelector('[data-file-count]');
    const slots = Array.from(
      upload.querySelectorAll('[data-file-slot]')
    );

    const maxFiles = Number(upload.dataset.maxFiles || 10);

    state.urls.forEach(url => URL.revokeObjectURL(url));
    state.urls = [];

    // Each file is submitted as its own Shopify line item property.
    slots.forEach((slot, index) => {
      slot.disabled = true;
      slot.value = '';

      const file = state.files[index];
      if (!file) return;

      const transfer = new DataTransfer();
      transfer.items.add(file);

      slot.files = transfer.files;
      slot.disabled = false;
    });

    if (count) {
      count.textContent =
        `${state.files.length} / ${maxFiles} photos selected`;
    }

    if (!preview) return;

    preview.replaceChildren();

    state.files.forEach((file, index) => {
      const card = document.createElement('div');
      card.className = 'personalization-photo-card';

      const image = document.createElement('img');
      const url = URL.createObjectURL(file);

      state.urls.push(url);

      image.src = url;
      image.alt = `Selected photo ${index + 1}`;
      image.loading = 'lazy';

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = '\u00D7';
      remove.dataset.removePhoto = String(index);

      remove.setAttribute(
        'aria-label',
        `Remove photo ${index + 1}`
      );

      const filename = document.createElement('small');
      filename.textContent = file.name;

      card.append(image, remove, filename);
      preview.appendChild(card);
    });
  }
}

if (!customElements.get('product-personalization')) {
  customElements.define(
    'product-personalization',
    ProductPersonalization
  );
}
